import { ControlsLayer } from "@manager/layer/ControlsLayer";
import { ControlsLayerObserver } from "@manager/layerObserver/ControlsLayerObserver";
import React from "react";
import { VintomError } from "src/api/model/error/VintomError";
import { I18n } from "../../i18n/i18n";
import { PlayerControls } from "../../model/playerConfiguration/PlayerControls";
import { ScenarioElement } from "../../model/playerConfiguration/ScenarioElement";
import { Subtitle } from "../../model/playerConfiguration/Subtitle";
import { QualityLevel } from "../../model/qualityLevel/QualityLevel";
import { ScenarioPath } from "../../model/scenarioPath/ScenarioPath";
import { SubtitlesText } from "../../model/subtitles/SubtitlesText";
import { ScenarioElementPlaybackState } from "../../uiModel/playback/ScenarioElementPlaybackState";
import { SocialShareConfiguration } from "../../uiModel/socialShare/SocialShareConfiguration";
import { DesktopControlsLayerComponent } from "./controls/desktop/DesktopControlsLayerComponent";
import { MobileControlsLayerComponent } from "./controls/mobile/MobileControlsLayerComponent";

import "@styles/c-controlsLayer.scss";

interface Props {
  className?: string;
  i18n: I18n;
  isMobileDevice: boolean;
  isFullscreenSupported: boolean;
  isFullscreen: boolean;
  socialShareConfiguration: SocialShareConfiguration;
}

interface State {
  configuration?: PlayerControls;
  isAdminMode: boolean;
  qualityLevels?: QualityLevel[];
  bandwidth?: number;
  isLoading: boolean;
  loaderTrigger: string;
  isPlaybackBlockingCTADisplayed: boolean;

  totalTime: number;
  ellapsedTime: number;
  bufferedTime: number;

  playbackState: ScenarioElementPlaybackState;
  scenarioPath: ScenarioPath;

  playbackSpeed: number;
  isDebugMode: boolean;

  volume: number;
  isVolumeMuted: boolean;

  subtitleSources: Subtitle[];
  selectedSubtitlesSrc?: string;
  subtitlesText?: SubtitlesText;
  message?: string | VintomError;
  disableIcon: boolean;
}

export class ControlsLayerComponent extends React.Component<Props, State> implements ControlsLayer {
  private static DEFAULT_VOLUME_AFTER_UNMUTE = 0.5;

  private observer: ControlsLayerObserver;
  private deviceSpecificLayer: DesktopControlsLayerComponent | MobileControlsLayerComponent;

  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: true,
      loaderTrigger: undefined,
      isAdminMode: false,
      isPlaybackBlockingCTADisplayed: false,

      playbackState: "paused",
      scenarioPath: undefined,

      totalTime: 0,
      ellapsedTime: 0,
      bufferedTime: 0,
      playbackSpeed: 1,
      isDebugMode: false,

      volume: 0,
      isVolumeMuted: false,

      subtitleSources: [],
      disableIcon: true,
    };
  }

  render() {
    const {
      configuration,
      isAdminMode,
      bandwidth,
      qualityLevels,
      isLoading,
      loaderTrigger,
      isPlaybackBlockingCTADisplayed,

      totalTime,
      ellapsedTime,
      bufferedTime,

      playbackState,
      scenarioPath,

      playbackSpeed,
      isDebugMode,

      volume,
      isVolumeMuted,

      subtitleSources,
      selectedSubtitlesSrc,
      subtitlesText,
      message,
      disableIcon,
    } = this.state;

    return (
      <div className={`${this.props.className || ""} c-controlsLayer`}>
        {configuration &&
          (this.props.isMobileDevice ? (
            <MobileControlsLayerComponent
              ref={(element) => (this.deviceSpecificLayer = element)}
              i18n={this.props.i18n}
              configuration={configuration}
              isLoading={isLoading}
              loaderTrigger={loaderTrigger}
              isCTADisplayed={isPlaybackBlockingCTADisplayed}
              socialShareConfiguration={this.props.socialShareConfiguration}
              playIconOnPoster={configuration.playIconOnPoster}
              disableIcon={disableIcon}
              playbackState={playbackState}
              onPlay={() => this.play()}
              onPause={() => this.pause()}
              onReplay={() => this.replay()}
              onSeekStart={() => this.startSeek()}
              onSeekEnd={() => this.endSeek()}
              scenarioPath={scenarioPath}
              totalTime={totalTime}
              ellapsedTime={ellapsedTime}
              bufferedTime={bufferedTime}
              onSeekTo={(time) => this.handleSeekTo(time)}
              onJumpToScenarioElement={(scElement, progress) => this.jumpToScenarioElement(scElement, progress)}
              isVolumeMuted={isVolumeMuted}
              onToggleMuted={() => this.onToggleMuted()}
              subtitles={subtitleSources}
              selectedSubtitlesSrc={selectedSubtitlesSrc}
              subtitlesText={subtitlesText}
              onSubtitleSourceSelected={(subtitle) => this.selectSubtitleSource(subtitle)}
              isFullscreenSupported={this.props.isFullscreenSupported}
              isFullscreen={this.props.isFullscreen}
              onToggleFullscreen={() => this.toggleFullscreen()}
              message={message}
              onSendError={(err) => this.observer.onSendError(err)}
            />
          ) : (
            <DesktopControlsLayerComponent
              ref={(element) => (this.deviceSpecificLayer = element)}
              i18n={this.props.i18n}
              configuration={configuration}
              isAdminMode={isAdminMode}
              bandwidth={bandwidth}
              qualityLevels={qualityLevels}
              isLoading={isLoading}
              loaderTrigger={loaderTrigger}
              isCTADisplayed={isPlaybackBlockingCTADisplayed}
              socialShareConfiguration={this.props.socialShareConfiguration}
              playIconOnPoster={configuration.playIconOnPoster}
              disableIcon={disableIcon}
              playbackState={playbackState}
              onPlay={() => this.play()}
              onPause={() => this.pause()}
              onReplay={() => this.replay()}
              onSeekStart={() => this.startSeek()}
              onSeekEnd={() => this.endSeek()}
              scenarioPath={scenarioPath}
              totalTime={totalTime}
              ellapsedTime={ellapsedTime}
              bufferedTime={bufferedTime}
              onSeekTo={(time) => this.handleSeekTo(time)}
              onJumpToScenarioElement={(scElement, progress) => this.jumpToScenarioElement(scElement, progress)}
              isVolumeMuted={isVolumeMuted}
              volume={volume}
              onVolumeChange={(vol) => this.changeVolume(vol)}
              onToggleMuted={() => this.onToggleMuted()}
              onUnMute={() => this.unMute()}
              subtitles={subtitleSources}
              selectedSubtitlesSrc={selectedSubtitlesSrc}
              subtitlesText={subtitlesText}
              onSubtitleSourceSelected={(subtitle) => this.selectSubtitleSource(subtitle)}
              onPlaybackSpeedChange={(speed) => this.setPlaybackSpeed(speed)}
              playbackSpeed={playbackSpeed}
              isDebugMode={isDebugMode}
              onToggleDebugMode={() => this.toggleDebugMode()}
              isFullscreenSupported={this.props.isFullscreenSupported}
              isFullscreen={this.props.isFullscreen}
              onToggleFullscreen={() => this.toggleFullscreen()}
              message={message}
              onSendError={(err) => this.observer.onSendError(err)}
            />
          ))}
      </div>
    );
  }

  // start of ControlsLayer implementation
  setConfiguration(configuration: PlayerControls, isAdminMode: boolean) {
    this.setState({
      configuration,
      isAdminMode,
    });
  }

  registerObserver(observer: ControlsLayerObserver): void {
    this.observer = observer;
  }

  resetScenarioElement(scenarioPath: ScenarioPath, subtitles?: Subtitle[]): void {
    this.setState({
      scenarioPath,
      bufferedTime: 0,
      ellapsedTime: 0,
      subtitleSources: subtitles,
      subtitlesText: undefined,
    });
  }

  loop() {
    this.setState({
      playbackState: "looping",
      disableIcon: false,
    });
  }

  play() {
    this.setState({
      playbackState: "playing",
      disableIcon: false,
    });
    this.observer.onPlay();
  }

  pause() {
    this.setState({
      playbackState: "paused",
    });
    this.observer.onPause();
  }

  setPlaybackSpeed(speed: number): void {
    this.setState({
      playbackSpeed: speed,
    });
    // this.observer.onPlaybackSpeedChange(speed);
  }

  toggleDebugMode() {
    this.setState({
      isDebugMode: !this.state.isDebugMode,
    });

    this.observer.onToggleDebugMode(!this.state.isDebugMode);
  }

  showLoader(loaderTrigger: string): void {
    this.setState({
      isLoading: true,
      loaderTrigger,
    });
  }

  hideLoader(): void {
    this.setState({
      isLoading: false,
      loaderTrigger: undefined,
    });
  }

  moveTo(time: number): void {
    this.setState({
      ellapsedTime: time,
    });
  }

  updateBufferedTime(time: number): void {
    this.setState({
      bufferedTime: time,
    });
  }

  setVolume(volume: number): void {
    this.setState({
      volume,
    });
  }

  setIsMuted(isMuted: boolean): void {
    this.setState({
      isVolumeMuted: isMuted,
    });
  }

  setSubtitleSources(subtitleSources: Subtitle[]) {
    this.setState({
      subtitleSources,
    });
  }

  selectSubtitleSource(subtitle: Subtitle): void {
    this.setState({
      selectedSubtitlesSrc: subtitle && subtitle.source,
    });
    this.observer.onSubtitleSourceSelected(subtitle);
  }

  setTotalScenarioTime(totalTime: number) {
    this.setState({
      totalTime,
    });
  }

  displaySubtitles(subtitles: SubtitlesText) {
    this.setState({
      subtitlesText: subtitles,
    });
  }

  scenarioEnded() {
    if (["seekingAfterPause", "seekingAfterPlay"].indexOf(this.state.playbackState) > -1) {
      return;
    }
    if (this.state.scenarioPath.isLastPart) {
      this.setState({
        playbackState: "scenarioFinished",
      });
    } else {
      this.setState({
        playbackState: "elementFinished",
      });
    }
  }

  userInteractionEnded() {
    this.deviceSpecificLayer.handleUserInteractionEnded();
  }

  userInteractedWithPlayer() {
    this.deviceSpecificLayer.handleUserInteractedWithPlayer();
  }

  userClickedPlayer() {
    this.deviceSpecificLayer.handleUserClickedPlayer();
  }

  showPlayerInfoTooltip(x: number, y: number) {
    if (!this.props.isMobileDevice) {
      (this.deviceSpecificLayer as DesktopControlsLayerComponent).showPlayerInfoTooltip(x, y);
    }
  }

  showMessage(message: string | VintomError): void {
    this.setState({
      message,
    });
  }

  setBandwidth(bandwidth: number): void {
    this.setState({
      bandwidth,
    });
  }

  setQualityLevels(qualityLevels: QualityLevel[]): void {
    this.setState({
      qualityLevels,
    });
  }

  setIsPlaybackBlockingCTADisplayed(isPlaybackBlockingCTADisplayed: boolean): void {
    this.setState({
      isPlaybackBlockingCTADisplayed,
    });
  }
  // end of ControlsLayer implementation

  private startSeek() {
    this.observer.onSeekStart();
    if (this.state.playbackState === "playing") {
      this.setState({
        playbackState: "seekingAfterPlay",
      });
    } else {
      this.setState({
        playbackState: "seekingAfterPause",
      });
    }
  }

  private endSeek() {
    const { ellapsedTime, totalTime, scenarioPath } = this.state;
    if (ellapsedTime >= totalTime) {
      this.observer.onReloadFrame();
      if (scenarioPath.isLastPart) {
        this.setState({
          playbackState: "scenarioFinished",
        });
      } else {
        this.setState({
          playbackState: "elementFinished",
        });
      }
    } else if (this.state.playbackState === "seekingAfterPlay") {
      this.observer.onPlay();
      this.setState({
        playbackState: "playing",
      });
    } else {
      this.observer.onReloadFrame();
      this.setState({
        playbackState: "paused",
      });
    }
  }

  private handleSeekTo(time: number) {
    this.observer.onSeekTo(time);
  }

  private replay() {
    this.observer.onJumpToScenarioElement(this.state.scenarioPath.initialElement, 0);
  }

  private changeVolume(volume: number) {
    const isVolumeMuted = volume === 0;
    this.setState({
      volume,
      isVolumeMuted,
    });
    this.observer.onVolumeChange(volume);
    this.observer.onMutedChange(isVolumeMuted);
  }

  private onToggleMuted() {
    this.updateMuted(!this.state.isVolumeMuted);
  }

  private unMute() {
    this.updateMuted(false);
  }

  private updateMuted(isVolumeMuted: boolean) {
    this.observer.onMutedChange(isVolumeMuted);
    this.setState({
      isVolumeMuted,
    });

    if (!isVolumeMuted) {
      const volume = this.state.volume || ControlsLayerComponent.DEFAULT_VOLUME_AFTER_UNMUTE;
      this.observer.onVolumeChange(volume);
      this.setState({
        volume,
      });
    }
  }

  private jumpToScenarioElement(scElement: ScenarioElement, progress: number): void {
    this.observer.onJumpToScenarioElement(scElement, progress);
  }

  private toggleFullscreen() {
    this.observer.onToggleFullscreen();
  }
}
